import React, { FC, Suspense, useCallback } from 'react';
import { Dialog, DialogWindow, DialogWindowClose, useDialogManager } from 'src/components/widgets/dialog';
import Loader from 'src/components/widgets/loader';
import ServiceCredits from 'src/components/pages/myAccount/serviceCredits';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';

const NO_CREDITS_DIALOG_ID = 'side-images-no-credits';

// Borderless window so the AI Studio purple header can bleed to the rounded top edge. The shared close icon
// is recolored white to stay legible over the gradient header.
const SideImagesModalWindow = styled(DialogWindow)`
    padding: 0;
    width: 640px;
    flex-basis: 640px;
    max-width: 640px;
    border-radius: 14px;
    overflow: hidden;
    /* Leave clearance above/below (on top of the overlay padding) so the modal never touches the
       window edges and the dimmed overlay itself never needs to scroll. */
    max-height: calc(100vh - 120px);

    ${DialogWindowClose} {
        color: #ffffff;
        fill: #ffffff;
        right: 20px;
        top: 20px;

        path {
            fill: #ffffff;
        }
    }
`;

interface SideImagesNoCreditsDialogProps {
    onClose: () => void;
}

/**
 * Product-setup "You're out of AI credits" dialog. Shown when generating side images (Print Areas / Variants
 * steps) runs the AI_STUDIO credit pool dry. It embeds the shared ServiceCredits purchase flow through the
 * bespoke 'aiStudioModal' variant so the seller can top up without leaving the product setup.
 */
const SideImagesNoCreditsDialog: FC<SideImagesNoCreditsDialogProps> = ({ onClose }) => {
    const { subscription, ecommerceName, shopifyEcommerceSettings, salesChannels } = useSubscriptionData();
    const isShopify = ecommerceName === 'SHOPIFY';
    const isTrial = subscription?.inTrial ?? false;
    const isShopifyTestStore = isShopify && shopifyEcommerceSettings?.isTestStore === true && config.isProduction;
    const isDisabled = (isTrial && !isShopify) || isShopifyTestStore;

    return (
        <Dialog windowDecorator={SideImagesModalWindow} onClose={onClose}>
            <Suspense fallback={<Loader />}>
                <ServiceCredits
                    variant='aiStudioModal'
                    onPurchaseCompleted={onClose}
                    services={[
                        {
                            serviceName: 'AI_STUDIO',
                            addonKey: 'AI_STUDIO',
                            displayName: 'AI Agents Studio',
                            description: <>{T._('Generate visuals, videos and 3D models with AI Agents Studio tools.', 'Admin')}</>,
                            learnMoreUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio',
                            addonCurrencyCode: 'USD',
                            disabled: isDisabled,
                            disabledMessage: isShopifyTestStore
                                ? T._('Sorry! Your account is currently a development store, which means you can not purchase credits.', 'Admin')
                                : isTrial
                                  ? T._('Unable to purchase credits during the trial period.', 'Admin')
                                  : undefined,
                            notAvailableInTrial: isDisabled
                        }
                    ]}
                    subscriptionCurrencyCode={
                        isShopify ? (salesChannels.at(0)?.ecommerceSettings?.currencyCode ?? 'USD') : (subscription?.plan?.currencyCode ?? 'USD')
                    }
                    isShopify={isShopify}
                    multiservice={false}
                />
            </Suspense>
        </Dialog>
    );
};

/** Returns a stable callback that opens the product-setup side-images no-credits purchase dialog. */
export function useSideImagesNoCreditsDialog(): { showSideImagesNoCreditsDialog: () => void } {
    const { showDialog, closeDialog } = useDialogManager();

    const showSideImagesNoCreditsDialog = useCallback(() => {
        showDialog(NO_CREDITS_DIALOG_ID, <SideImagesNoCreditsDialog onClose={() => closeDialog(NO_CREDITS_DIALOG_ID)} />);
    }, [showDialog, closeDialog]);

    return { showSideImagesNoCreditsDialog };
}
